/**
 * CLE-2811 — every user-visible string that names the thing a user creates in the AI contract modeling
 * tool. The code, the routes and the API call it a "run"; the product calls it a "contract model". Say
 * it here and nowhere else, so the next rename is a one-file change.
 *
 * "Model" on its own is used only where the tool's own page leaves no doubt (its buttons and toasts).
 * Headings, tabs and empty states say "contract model", so it is never mistaken for the Chatbot page's
 * chat models.
 */

/** The entity mid-sentence, for helpers that build a sentence around it. */
export const CONTRACT_MODEL = "contract model";

// ---------------------------------------------------------------------------------------------
// The list page
// ---------------------------------------------------------------------------------------------

/** The create button and the dialog it opens. */
export const NEW_CONTRACT_MODEL = "New contract model";

export const CONTRACT_MODELS_TAB = "Contract models";

export const CONTRACT_MODELS_EMPTY =
  "No contract models yet. Click “New contract model” to start.";

// ---------------------------------------------------------------------------------------------
// The detail page
// ---------------------------------------------------------------------------------------------

/** The short id under the heading — what you quote in a bug report — and the unnamed fallback title. */
export const modelIdLabel = (id: string): string => `Model ${id.slice(0, 8)}`;

export const LOADING_CONTRACT_MODEL = "Loading contract model…";

/** Back links: to the contract page it was opened from, else to the list. */
export const BACK_TO_CONTRACT = "Back to contract";
export const BACK_TO_CONTRACT_MODELS = "Back to contract models";

// ---------------------------------------------------------------------------------------------
// Cancel and delete
// ---------------------------------------------------------------------------------------------

export const CANCEL_MODEL = "Cancel model";
export const CANCEL_MODEL_HEADER = "Cancel this model?";
export const CANCEL_MODEL_MESSAGE =
  "It stays here as a record, but nothing more will be generated for it. Model with AI on the contract will start a new contract model.";
export const MODEL_CANCELED = "Model canceled";
export const CANCEL_MODEL_FAILED = "Couldn't cancel the model";

export const DELETE_MODEL = "Delete model";
export const DELETE_MODEL_HEADER = "Delete this model?";
export const DELETE_MODEL_MESSAGE =
  "This removes the contract model and its generated output. Documents attached to the contract are kept.";
export const MODEL_DELETED = "Model deleted";
export const DELETE_MODEL_FAILED = "Couldn't delete the model";

// ---------------------------------------------------------------------------------------------
// Starting one (the "New contract model" dialog and "Model with AI" on a contract page)
// ---------------------------------------------------------------------------------------------

export const START_MODEL_FAILED = "Could not start the contract model";
export const START_MODEL_FAILED_DETAIL =
  "Something went wrong starting the contract model.";

export const NEW_MODEL_PROVIDER_HINT =
  "The contract model’s account is derived from this provider. Model one provider at a time — for an office/facility split, run it once per entity.";

/** Follows the existing contract's name in the dialog's "A contract already exists…" notice. */
export const NEW_MODEL_EXISTING_CONTRACT_HINT =
  "Modeling it ties the contract model to the contract, uses its attachments, and saves the facts back onto it.";

export const NEW_MODEL_FACTS_FIRST_HINT =
  "The contract model starts by reading the contract facts; you’ll upload documents on the next screen.";

// ---------------------------------------------------------------------------------------------
// Standing instructions
// ---------------------------------------------------------------------------------------------

export const STANDING_INSTRUCTIONS_INTRO =
  "Standing instructions are given to the AI for every contract model of that contract type, so you do not have to type them each time. When a contract model leaves the type to the AI, it gets every type’s instructions and applies the one for the type it finds.";

/** The explanation under each contract type's title. */
export const instructionExplanation = (typeLabel: string): string =>
  `Applied to every contract model built on a${
    /^[AEIOU]/i.test(typeLabel) ? "n" : ""
  } ${typeLabel} contract, in addition to what you type in the conversation.`;

// ---------------------------------------------------------------------------------------------
// The conversation and regeneration: a contract model has several passes
// ---------------------------------------------------------------------------------------------

/** The chip under her first bubble: the instructions she gave before the first generation. */
export const INITIAL_INSTRUCTIONS_CHIP = "Initial instructions";

/** The toast after a message sent while a pass is already working. */
export const QUEUED_TOAST_DETAIL = "It will run right after the current pass.";

/** Under the composer while her message waits for the pass in flight. */
export const QUEUED_COMPOSER_HINT =
  "Queued — it will run right after the current pass.";

/** The "Request a change" dialog's note on what happens mid-pass. */
export const REQUEST_CHANGE_QUEUED_HINT =
  "If it is already working, it runs right after the current pass.";

/** The What Changed step before the first regeneration. */
export const NOT_REGENERATED_YET =
  "This contract model hasn’t been regenerated yet, so there’s nothing to compare against. Add an instruction or a document, then regenerate.";
